import { useState } from "react"
import { callMcpTool, type ToolName } from "../api/mcp-playground.ts"
import type { AuthContext } from "../components/AdminAuthGate.tsx"

/** `warnings.aliased` 단일 항목 — KNOWLEDGE_UPSERT_OUTPUT.warnings.aliased 와 동형 */
type AliasedEdge = {
	readonly fromId: string
	readonly toId: string
	readonly input: string
	readonly canonical: string
}

/** result 에서 `warnings.aliased` 배열을 안전하게 추출 */
function extractAliasedWarnings(result: unknown): ReadonlyArray<AliasedEdge> | undefined {
	if (typeof result !== "object" || result === null) return undefined
	const w = (result as Record<string, unknown>).warnings
	if (typeof w !== "object" || w === null) return undefined
	const aliased = (w as Record<string, unknown>).aliased
	if (!Array.isArray(aliased) || aliased.length === 0) return undefined
	return aliased as ReadonlyArray<AliasedEdge>
}

const IS_MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.userAgent)

const DEFAULT_INPUTS: Record<ToolName, string> = {
	knowledge_search: JSON.stringify({ query: "example", limit: 10 }, null, 2),
	knowledge_traverse: JSON.stringify({ startNodeId: "", depth: 1 }, null, 2),
	// alias 정규화 데모 — AFFILIATED_WITH 입력 → 응답 warnings.aliased 에 정규화 기록
	// (outgoingEdges[0].relation 을 슬라이스에 등록된 alias 로 바꾸면 canonical 로 자동 정규화됨)
	knowledge_upsert: JSON.stringify(
		{
			node: { nodeId: "", domain: "ops", kind: "concept", name: "" },
			outgoingEdges: [
				{
					toId: "",
					relation: "AFFILIATED_WITH"
				}
			]
		},
		null,
		2
	),
	knowledge_invalidate_node: JSON.stringify({ nodeId: "", reason: "" }, null, 2),
	docs_list: "{}",
	docs_search: JSON.stringify({ query: "ontology" }, null, 2),
	docs_read: JSON.stringify({ slug: "" }, null, 2),
	// V10 Phase C — edge-embedded episodes
	edge_attach_episode: JSON.stringify(
		{
			toEdges: [{ fromId: "node-a", toId: "node-b", relation: "FRIEND_OF" }],
			content: "첫 만남 (기차에서 개구리 초콜릿 공유)",
			source: "message",
			occurredAt: new Date().toISOString()
		},
		null,
		2
	),
	edge_link_episode: JSON.stringify({ episodeId: "", fromId: "", toId: "", relation: "FRIEND_OF" }, null, 2),
	edge_list_episodes: JSON.stringify(
		{ fromId: "node-a", toId: "node-b", relation: "FRIEND_OF", limit: 20 },
		null,
		2
	),
	edge_semantic_search: JSON.stringify({ query: "첫 만남", topK: 10 }, null, 2),
	edge_invalidate_episode: JSON.stringify(
		{ episodeId: "", reason: "사건 자체 부정 (책 개정판에서 해당 scene 삭제)" },
		null,
		2
	),
	edge_invalidate_link: JSON.stringify(
		{ episodeId: "", fromId: "", toId: "", relation: "FRIEND_OF", reason: "이 edge 에서만 의미 철회" },
		null,
		2
	),
	edge_invalidate: JSON.stringify(
		{ fromId: "", toId: "", relation: "FRIEND_OF", reason: "관계 종료" },
		null,
		2
	)
}

const TOOL_NAMES = Object.keys(DEFAULT_INPUTS) as ReadonlyArray<ToolName>

type RunMeta = {
	readonly tool: ToolName
	readonly ok: boolean
	readonly durationMs: number
	readonly responseChars: number
	readonly at: string
}

/**
 * MCP Playground — `/mcp/:slug/:server` 로 호출. `enabledTools` 가 전달되면 드롭다운을 그 범위로 제한.
 */
export function McpPlayground(props: {
	readonly auth: AuthContext
	readonly serviceSlug: string
	readonly serverName: string
	readonly apiKey: string | undefined
	readonly enabledTools?: ReadonlyArray<string>
}) {
	const allowedTools = props.enabledTools
		? (TOOL_NAMES.filter((t) => props.enabledTools?.includes(t)) as ReadonlyArray<ToolName>)
		: TOOL_NAMES
	const initialTool = allowedTools[0] ?? "knowledge_search"

	const [tool, setTool] = useState<ToolName>(initialTool)
	const [input, setInput] = useState<string>(DEFAULT_INPUTS[initialTool])
	const [apiKeyOverride, setApiKeyOverride] = useState<string>("")
	const [output, setOutput] = useState<string>("")
	/** 파싱된 응답 원본 — warnings.aliased 추출용 */
	const [lastResult, setLastResult] = useState<unknown>(undefined)
	const [meta, setMeta] = useState<RunMeta | undefined>()
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<string | undefined>()
	const [copied, setCopied] = useState(false)

	const effectiveKey = apiKeyOverride || (props.apiKey ?? "")
	const endpointPath = `/mcp/${props.serviceSlug}/${props.serverName}`

	async function run() {
		setBusy(true)
		setError(undefined)
		setCopied(false)
		setLastResult(undefined)
		const started = performance.now()
		let parsed: Record<string, unknown>
		try {
			parsed = JSON.parse(input) as Record<string, unknown>
		} catch (e) {
			setError(`JSON 파싱 실패: ${(e as Error).message}`)
			setBusy(false)
			return
		}
		try {
			const result = await callMcpTool({
				baseUrl: props.auth.baseUrl,
				serviceSlug: props.serviceSlug,
				serverName: props.serverName,
				apiKey: effectiveKey,
				tool,
				input: parsed
			})
			const pretty = JSON.stringify(result, null, 2)
			setOutput(pretty)
			setLastResult(result)
			setMeta({
				tool,
				ok: true,
				durationMs: Math.round(performance.now() - started),
				responseChars: pretty.length,
				at: new Date().toISOString()
			})
		} catch (e) {
			setError((e as Error).message)
			setMeta({
				tool,
				ok: false,
				durationMs: Math.round(performance.now() - started),
				responseChars: 0,
				at: new Date().toISOString()
			})
		} finally {
			setBusy(false)
		}
	}

	function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
		if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
			e.preventDefault()
			if (!busy && effectiveKey) void run()
		}
	}

	async function copyOutput() {
		if (!output) return
		try {
			await navigator.clipboard.writeText(output)
			setCopied(true)
			window.setTimeout(() => setCopied(false), 1400)
		} catch {
			/* ignore */
		}
	}

	return (
		<div className="panel">
			<h3>MCP 플레이그라운드</h3>
			<p style={{ marginTop: 0, marginBottom: 8 }}>
				<a
					href="/docs/playground"
					target="_blank"
					rel="noreferrer"
					style={{ fontWeight: 600, textDecoration: "none", color: "var(--pk-brand-500, #6366f1)" }}
				>
					로컬 플레이그라운드로 열기 →
				</a>
				<span className="muted" style={{ fontSize: 12, marginLeft: 8 }}>
					Sonnet 4.6이 에피소드를 어떻게 분해하는지 브라우저에서 바로 체험.
				</span>
			</p>
			<p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
				<code className="inline">{endpointPath}</code> 에 JSON-RPC <code className="inline">tools/call</code>{" "}
				요청을 보냅니다. 어드민 토큰이 아닌 <strong>서비스 API 키</strong>가 필요합니다.
			</p>

			<div className="field">
				<label className="field-label" htmlFor="api-key">
					API 키
				</label>
				<input
					id="api-key"
					className="input input-mono"
					type="password"
					placeholder={props.apiKey ? "발급된 키 사용 중 (덮어쓰려면 입력)" : "pk_..."}
					value={apiKeyOverride}
					onChange={(e) => setApiKeyOverride(e.target.value)}
				/>
				{props.apiKey && !apiKeyOverride && (
					<span className="field-hint success">위에서 방금 발급한 키가 자동 주입되어 있습니다.</span>
				)}
			</div>

			<div className="field">
				<label className="field-label" htmlFor="tool">
					도구
				</label>
				<div className="row">
					<select
						id="tool"
						className="input select"
						style={{ maxWidth: 260 }}
						value={tool}
						onChange={(e) => {
							const next = e.target.value as ToolName
							setTool(next)
							setInput(DEFAULT_INPUTS[next])
						}}
					>
						{allowedTools.map((t) => (
							<option key={t} value={t}>
								{t}
							</option>
						))}
					</select>
					<button type="button" className="btn btn-primary" disabled={busy || !effectiveKey} onClick={run}>
						{busy ? "실행 중..." : "실행"}
					</button>
					<span className="field-hint" style={{ marginLeft: 4 }}>
						{IS_MAC ? "⌘" : "Ctrl"} + Enter
					</span>
				</div>
			</div>

			<div className="field">
				<label className="field-label" htmlFor="input">
					입력 (JSON)
				</label>
				<textarea
					id="input"
					className="input"
					value={input}
					onChange={(e) => setInput(e.target.value)}
					onKeyDown={onKeyDown}
					spellCheck={false}
				/>
			</div>

			<div className="field">
				<div className="response-head">
					<div className="field-label" style={{ margin: 0 }}>
						응답
					</div>
					{meta && (
						<div className="response-meta">
							<span className={`dot ${meta.ok ? "dot-ok" : "dot-err"}`} aria-hidden="true" />
							<span className={meta.ok ? "success" : "danger"}>{meta.ok ? "성공" : "실패"}</span>
							<span className="muted">· {meta.durationMs} ms</span>
							{meta.ok && <span className="muted">· {formatBytes(meta.responseChars)}</span>}
						</div>
					)}
					{output && (
						<button type="button" className="btn btn-ghost btn-sm response-copy" onClick={copyOutput}>
							{copied ? "복사됨" : "복사"}
						</button>
					)}
				</div>
				{error && <div className="alert alert-danger response-alert">{error}</div>}
				{/* alias 정규화 경고 — warnings.aliased 가 있을 때만 렌더 */}
				{(() => {
					const aliased = extractAliasedWarnings(lastResult)
					if (!aliased) return null
					return (
						<div className="alert alert-warn alias-notice" style={{ marginBottom: 8 }}>
							<strong>관계 이름 정규화 발생</strong>
							<ul style={{ margin: "4px 0 0", paddingLeft: "1.2em" }}>
								{aliased.map((a) => (
									<li key={a.input}>
										<code className="inline">{a.input}</code> → <code className="inline">{a.canonical}</code>
									</li>
								))}
							</ul>
						</div>
					)
				})()}
				{output ? (
					<pre className="code response-pre">{output}</pre>
				) : !error ? (
					<div className="response-empty">
						아직 실행 결과가 없습니다. 위의 <strong>실행</strong> 버튼 또는 입력창에서 {IS_MAC ? "⌘" : "Ctrl"}{" "}
						+ Enter 로 호출하세요.
					</div>
				) : null}
			</div>
		</div>
	)
}

function formatBytes(chars: number): string {
	if (chars < 1024) return `${chars} B`
	return `${(chars / 1024).toFixed(1)} KB`
}
